import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APIEventSystem } from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { gsap } from "gsap";

import { AbstractReel, IReelData } from "../../../../abstracts/elements/AbstractReel";
import { AbstractSymbol } from "../../../../abstracts/elements/AbstractSymbol";
import { EventsManager } from "../../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../../abstracts/flow/AbstractGameMode";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { ReelPaddingData } from "../../../../abstracts/types/interfaces";
import { getRandomElement } from "../../../../abstracts/utils/JSUtils";
import { fadeAndStopSound, playSound } from "../../../../abstracts/utils/SoundUtils";
import {
    ApocalypseSoundNames,
    ApocalypseSpineNames,
} from "../../../assetsLoader/ApocalypseAssetsNames";
import {
    ApocalypseConfig,
    davidPanningReelStop,
    normalConfig,
    normalReelSet,
    reelsConfig,
    symbolConfig,
} from "../../../config/ApocalypseConfig";
import { ADD_FAKE_SCATTER, UPDATE_FAKE_SCATTER_POSITION } from "../../../events/ApocalypseEvents";
import { ApocalypseSymbolNames } from "../../../types/ApocalypseEnums";
import {
    ApocalypseFakeScatterData,
    SimpleReelModelEventData,
} from "../../../types/ApocalypseInterfaces";
import { ApocalypseBlankSymbol } from "../symbols/ApocalypseBlankSymbol";
import { ApocalypseScatterSymbol } from "../symbols/ApocalypseScatterSymbol";
import { ApocalypseSymbol } from "../symbols/ApocalypseSymbol";
import { getSymbolFromPool, returnSymbolToPool } from "../symbols/ApocalypseSymbolsPool";
import { ApocalypseWildSymbol } from "../symbols/ApocalypseWildSymbol";
import { ApocalypseXNudgeSymbol } from "../symbols/ApocalypseXNudgeSymbol";
import { ApocalypseAnticipation } from "./ApocalypseAnticipation";

export const reelLandSounds: ApocalypseSoundNames[] = [
    ApocalypseSoundNames.REEL_LAND_1,
    ApocalypseSoundNames.REEL_LAND_2,
    ApocalypseSoundNames.REEL_LAND_3,
    ApocalypseSoundNames.REEL_LAND_4,
    ApocalypseSoundNames.REEL_LAND_5,
    ApocalypseSoundNames.REEL_LAND_6,
];

export class ApocalypseReel extends AbstractReel {
    protected animatedStepData: { y: number };
    protected masterSpinTimeline: gsap.core.Timeline | undefined;
    protected nudgeSteps: number = 0;
    protected paddingConfig: ReelPaddingData;
    protected reelSetIndex: number = 0;
    protected isSpinning: boolean = false;
    protected stopIndex: number;
    protected stopping: boolean = false;
    protected stopTimeline: gsap.core.Timeline | undefined;
    protected stopTriggerTl: gsap.core.Timeline | undefined;
    protected triggerStop: boolean = false;
    protected visibleSymbolsCount: number = 1;
    protected customStepDuration: number = 0;

    private scatterSymbol: ApocalypseScatterSymbol | undefined;
    private xNudgeSymbol: ApocalypseXNudgeSymbol | undefined;

    public readonly events: APIEventSystem | undefined;

    private ghostSymbol: AbstractSpine;

    constructor(
        reelId: number,
        symbols: string[],
        gameMode: AbstractGameMode = AbstractGameMode.NORMAL
    ) {
        super(reelId, symbols, gameMode);

        this.name = `reel` + reelId.toString();

        this.events = SlotGame.apiPlugin.eventSystemFactory.create();
        this.events.on(`ReelStateChange`, (data: SimpleReelModelEventData) =>
            this.onReelStateChange(data)
        );

        this.customStepDuration = normalConfig.stepDuration!;
    }

    protected override get reelSet(): string[] {
        return this.normalReelSet;
    }

    protected getVisibleSymbolsCount(): number {
        this.visibleSymbolsCount = ApocalypseConfig.reelConfig.normalRowsNumber;

        return this.visibleSymbolsCount;
    }

    protected get stepSize(): number {
        return normalConfig.stepSize!;
    }

    protected override generateInitialSymbols(symbols: string[]): void {
        super.generateInitialSymbols(symbols);

        this.normalReelSet = normalReelSet;

        this.customStepDuration = normalConfig.stepDuration!;
        this.currentSymbols = [];
        this.stopSymbols = [];

        this.animatedStepData = { y: 0 };

        this.paddingConfig = {
            visualPaddingBefore: 1,
            paddingBefore: 0,
            visibleSymbols: this.getVisibleSymbolsCount(),
            paddingAfter: 0,
            visualPaddingAfter: 1,
        };

        const totalInitCount =
            this.paddingConfig.paddingBefore +
            this.paddingConfig.visibleSymbols +
            this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.reelId}`);
        }

        symbols = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(symbols);
        symbols = symbols.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

        for (let i: number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(
                symbols[i],
                i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore
            );

            this.currentSymbols.push(symbol);
        }

        this.updatePosition();
    }

    protected override initializeOtherGraphics() {
        super.initializeOtherGraphics();

        this.scatterNearWin = new ApocalypseAnticipation(this, `animation`);
        this.scatterNearWin.name = `scatterNearWin${this.reelId}`;
    }

    protected onReelStateChange(data: SimpleReelModelEventData): void {
        this.events?.trigger(data.state, data);
    }

    protected getNextSymbol(position: number): AbstractSymbol {
        if (this.stopping && this.stopNormalBetAnimation.length > 0) {
            const symbol = this.stopSymbols[this.stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }

        return this.createNewSymbol(getRandomElement(this.reelSet), position);
    }

    protected createNewSymbol(name: string, position: number): AbstractSymbol {
        const symbol = getSymbolFromPool(name);

        this.symbolsContainer.addChild(symbol);
        symbol.reelPosition = position;

        if (symbol instanceof ApocalypseWildSymbol) {
            (symbol as ApocalypseWildSymbol).changeSymbolTexture(this.reelId);
        }

        return symbol;
    }

    protected getRandomSymbolInReelSet(): string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        return this.reelSet[randomIndex];
    }

    protected clearSymbols(symbols: AbstractSymbol[]): void {
        let symbol;

        // eslint-disable-next-line no-cond-assign
        while ((symbol = symbols.pop())) {
            returnSymbolToPool(symbol);
        }
    }

    protected getRandomSymbols(count: number): string[] {
        const result: string[] = [];
        for (let i: number = 0; i < count; i++) {
            result.push(this.getRandomSymbolInReelSet());
        }
        return result;
    }

    public override startNormalBetAnimation(): gsap.core.Timeline {
        this.customStepDuration = normalConfig.stepDuration!;

        this.scatterSymbol = undefined;
        this.xNudgeSymbol = undefined;

        const tl = gsap.timeline({
            onStart: () => {
                this.stopSymbols = [];
                this.nudgeSteps = 0;
            },
            onComplete: () => {
                this.startSpin();
            },
        });

        const tween = this.step();
        tween.vars.ease = `power2.in`;
        tween.totalDuration(normalConfig.stepDuration! * 4);

        tl.add(tween);

        return tl;
    }

    protected getEaseFactor(): number {
        // ! is fast spin active fa event
        this.customStepDuration = SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)
            ? normalConfig.stepDuration! - 0.03
            : normalConfig.stepDuration!;
        return SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 2.8 : 1.8;
    }

    protected getStopDurationFactor() {
        // ! is fast spin active fa event
        return SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)
            ? normalConfig.stepDuration! - 0.01
            : normalConfig.stepDuration!;
    }

    public override stopNormalBetAnimation(
        data: IReelData,
        onReelComplete: Function | undefined = undefined,
        onLandStart: Function = () => {},
        hasTeasing: boolean = false
    ): gsap.core.Timeline {
        this.clearSymbols(this.stopSymbols);

        this.customStepDuration = this.getStopDurationFactor();
        this.stopSymbols = [];

        data.stopSymbolNames = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(
            data.stopSymbolNames!
        );
        data.stopSymbolNames = data.stopSymbolNames.concat(
            this.getRandomSymbols(this.paddingConfig.visualPaddingAfter)
        );

        for (let i: number = 0; i < data.stopSymbolNames.length; i++) {
            const symbol = this.createNewSymbol(
                data.stopSymbolNames[i],
                i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore
            );
            symbol.y =
                this.stepSize *
                -(
                    this.paddingConfig.visualPaddingBefore +
                    this.paddingConfig.paddingBefore +
                    this.paddingConfig.visibleSymbols
                );

            if (symbol instanceof ApocalypseScatterSymbol && i > 0 && i < 5) {
                this.scatterSymbol = symbol;
            }

            if (symbol instanceof ApocalypseXNudgeSymbol && i > 0 && i < 5) {
                this.xNudgeSymbol = symbol;
            }

            this.stopSymbols.push(symbol);
        }

        this.stopTimeline = gsap.timeline({
            paused: true,
            onStart: () => {
                this.stopIndex = data.stopSymbolNames!.length - 1;
                this.stopping = true;

                // Gradually reduce the timescale until it reaches 0
                gsap.to(this.stopTimeline!, {
                    duration: this.getEaseFactor(),
                    timeScale: 0.1,
                });
            },
            onComplete: () => {
                this.stopTimeline = undefined;
                this.stopping = false;
            },
        });

        const numberOfNormalSteps =
            this.paddingConfig.visualPaddingBefore +
            this.paddingConfig.paddingBefore +
            this.paddingConfig.visibleSymbols +
            this.paddingConfig.paddingAfter +
            this.paddingConfig.visualPaddingAfter;

        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            const tween = this.step();
            this.stopTimeline.add(tween);
        }

        this.stopTimeline.add(() => {
            this.bounce(onReelComplete, onLandStart, hasTeasing);
        });

        this.stopTriggerTl = gsap.timeline({
            onStart: () => {
                this.isSpinning = false;
                this.triggerStop = true;
            },
        });

        this.stopTriggerTl.add(
            gsap.to(this, {
                duration: this.stopTimeline.totalDuration(),
            })
        );

        const teaseBlockedSymbolsLabel: string = `teaseBlockedSymbols`;

        this.stopTriggerTl.addLabel(teaseBlockedSymbolsLabel);

        return this.stopTriggerTl;
    }

    protected bounce(
        onReelComplete: Function = () => {},
        onLandStart: Function = () => {},
        hasTeasing: boolean = false
    ): gsap.core.Timeline {
        const tl = gsap.timeline();

        const updateFakeScatterPosition = () => {
            if (this.scatterSymbol !== undefined) {
                const scatterSymbolGlobalPosition = this.scatterSymbol.getGlobalPosition();
                const scatterData: ApocalypseFakeScatterData = {
                    reelIndex: this.reelId,
                    symbolGlobalPosition: {
                        x: scatterSymbolGlobalPosition.x,
                        y: scatterSymbolGlobalPosition.y,
                    },
                };
                EventsManager.trigger(UPDATE_FAKE_SCATTER_POSITION, scatterData);
            }
        };

        const overshoot = gsap.to(this.animatedStepData, {
            duration: 0.06,
            y: this.stepSize / 16,
            onStart: () => {
                if (this.scatterSymbol) {
                    EventsManager.trigger(ADD_FAKE_SCATTER, {
                        reelIndex: this.reelId,
                    } as ApocalypseFakeScatterData);
                    updateFakeScatterPosition();
                }

                onLandStart(this.reelId);

                playSound({
                    name: reelLandSounds[this.reelId],
                    stereo: davidPanningReelStop[this.reelId],
                });

                if (this.reelId === reelsConfig.reelsNumber - 1) {
                    gsap.delayedCall(0.15, () => {
                        fadeAndStopSound({
                            name: ApocalypseSoundNames.REELS_SPIN,
                            from: 1,
                            to: 0,
                            duration: 0.11,
                        });
                        //
                        fadeAndStopSound({
                            name: ApocalypseSoundNames.SCATTER_ANTICIPATION,
                            duration: 0.2,
                            from: 1,
                            to: 0,
                        });
                    });
                }
            },
            onUpdate: () => {
                this.updatePosition();

                updateFakeScatterPosition();
            },
            onComplete: () => {},
        });

        const back = gsap.to(this.animatedStepData, {
            duration: 0.1,
            y: 0,
            ease: `power1.in`,
            onStart: () => {
                this.handleSoundsOnLand();

                gsap.to(this.stopSymbols[5], {
                    alpha: 0,
                    duration: 0.2,
                });
            },
            onUpdate: () => {
                this.updatePosition();

                updateFakeScatterPosition();
            },
            onComplete: () => {
                updateFakeScatterPosition();

                onReelComplete(this.reelId);
            },
        });

        tl.add(overshoot);
        tl.add(back);

        const landingTimeline = gsap.timeline({});

        this.currentSymbols.forEach((symbol, symbolIndex) => {
            if (
                (this.reelId === 2 || this.reelId === 3) &&
                symbolIndex > 0 &&
                symbolIndex < 5 &&
                symbol instanceof ApocalypseWildSymbol
            ) {
                landingTimeline.add(symbol.getLandingAnimation(), 0);
            }

            if (symbolIndex > 0 && symbolIndex < 5 && symbol instanceof ApocalypseXNudgeSymbol) {
                landingTimeline.add(symbol.getLandingAnimation(), 0);
            }

            // ! tre facut si cu xnudge
        });
        landingTimeline.totalDuration(tl.totalDuration() * 0.9);

        tl.add(landingTimeline, 0);

        return tl;
    }

    protected step(): gsap.core.Tween {
        const tween: gsap.core.Tween = gsap.to(this.animatedStepData, {
            duration: this.customStepDuration,
            y: this.stepSize,
            ease: `none`,
            onStart: () => this.addSymbol(),
            onUpdate: () => {
                this.updatePosition();
            },
            onComplete: () => this.onAnimationComplete(),
        });

        return tween;
    }

    protected spin(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                if (this.isSpinning) {
                    this.masterSpinTimeline?.add(this.spin());
                }
            },
        });
        tl.add(this.step());
        return tl;
    }

    protected startSpin() {
        this.isSpinning = true;
        this.masterSpinTimeline = gsap.timeline({});
        this.masterSpinTimeline.add(this.spin());

        this.currentSymbols.forEach((symbol) => {
            (symbol as ApocalypseSymbol).isSpinning = true;
        });
    }

    protected onAnimationComplete() {
        for (let i: number = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.reelPosition += 1;
            symbol.positionInArray = i;
        }

        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this.stopTimeline?.resume();
        }

        this.updateIndex();
        this.removeSymbolFromReel();
        this.updatePosition();
    }

    protected updateIndex() {
        const valueChange = -1;
        if (this.stopping) {
            this.setStopIndex(this.stopIndex + valueChange);
        } else {
            this.setReelIndex(this.reelSetIndex + valueChange);
        }
    }

    protected setStopIndex(value: number) {
        if (value >= this.stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this.stopSymbols.length - 1;
        }
        this.stopIndex = value;
    }

    protected setReelIndex(value: number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this.reelSetIndex = value;
    }

    protected addSymbol() {
        let newSymbols = [];

        const newSymbolPosition = this.currentSymbols[0].reelPosition - 1;

        const newSymbol = this.getNextSymbol(newSymbolPosition);

        newSymbols.push(newSymbol);

        this.currentSymbols = this.currentSymbols.concat(newSymbols);
        this.currentSymbols.sort((a: any, b: any) => {
            return a.reelPosition < b.reelPosition ? -1 : 1;
        });

        if (this.isSpinning) {
            this.currentSymbols.forEach((symbol) => {
                (symbol as ApocalypseSymbol).isSpinning = true;
            });
        }
    }

    protected removeSymbolFromReel() {
        for (let i: number = this.currentSymbols.length - 1; i >= 0; i--) {
            const symbol = this.currentSymbols[i];
            if (
                symbol.reelPosition >=
                this.paddingConfig.visibleSymbols +
                    this.paddingConfig.paddingAfter +
                    this.paddingConfig.visualPaddingAfter
            ) {
                const removedSymbol = this.currentSymbols.splice(i, 1)[0];

                returnSymbolToPool(removedSymbol);
            }
            if (
                symbol.reelPosition <
                -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore
            ) {
                //Remove from top
                const removedSymbol = this.currentSymbols.splice(i, 1)[0];
                returnSymbolToPool(removedSymbol);
            }
        }
    }

    protected updatePosition() {
        for (let i: number = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            const basePos = this.stepSize * symbol.reelPosition;

            symbol.y = basePos + this.animatedStepData.y;
        }
    }

    public override getWinningSymbols(winningArr: boolean[]): ApocalypseSymbol[] {
        const visibleSymbols = this.getAllVisibleStopSymbols();

        const winningSymbols: ApocalypseSymbol[] = [];

        visibleSymbols.forEach((symbol, index) => {
            if (winningArr[index]) {
                winningSymbols.push(symbol as ApocalypseSymbol);
            }
        });

        return winningSymbols;
    }

    public getNonWinningSymbols(winningArr: boolean[]): ApocalypseSymbol[] {
        const visibleSymbols = this.getAllVisibleStopSymbols();

        const nonWinningSymbols: ApocalypseSymbol[] = [];

        visibleSymbols.forEach((symbol, index) => {
            if (!winningArr[index]) {
                nonWinningSymbols.push(symbol as ApocalypseSymbol);
            }
        });

        return nonWinningSymbols;
    }

    public getAllVisibleStopSymbols(): AbstractSymbol[] {
        const paddingBefore =
            this.paddingConfig.visualPaddingBefore +
            this.paddingConfig.paddingBefore +
            this.nudgeSteps;

        if (this.stopSymbols.length > 0) {
            return this.stopSymbols.slice(
                paddingBefore,
                paddingBefore + this.paddingConfig.visibleSymbols
            );
        }

        return this.currentSymbols.slice(
            paddingBefore,
            paddingBefore + this.paddingConfig.visibleSymbols
        );
    }

    public override getShowNearWinAnimation(
        hasWild: boolean = false,
        wildSymbolPosition: number = -1
    ): gsap.core.Tween | gsap.core.Timeline {
        const showNearWinAnimation = super.getShowNearWinAnimation();

        this.scatterNearWin.alpha = 1;
        const speedObj: { value: number } = { value: normalConfig.stepDuration! };

        const speedUpSpin = gsap.to(speedObj, {
            value: normalConfig.stepDuration! * 0.7,
            duration: normalConfig.nearWinDuration! / 2,
            onUpdate: () => {
                this.customStepDuration = speedObj.value;
            },
        });

        const tl = gsap.timeline();
        tl.add(showNearWinAnimation, 0);
        tl.add(speedUpSpin, 0);

        if (hasWild) {
            if (!this.ghostSymbol) {
                this.ghostSymbol = new AbstractSpine(
                    ApocalypseSpineNames.GHOST_SYMBOL_ANTICIPATION
                );
                // this.ghostSymbol.setSkinByName(`Wild2`);
                this.ghostSymbol.name = `ghostSymbol`;
                this.ghostSymbol.x = symbolConfig.symbolWidth / 2;
                // this.ghostSymbol.alpha = 0.4;
            }

            this.ghostSymbol.y =
                wildSymbolPosition * (symbolConfig.symbolHeight + symbolConfig.symbolSpacing) +
                symbolConfig.symbolHeight / 2 +
                3;

            tl.call(
                () => {
                    this.scatterNearWin.alpha = 0.6;

                    this.addChild(this.ghostSymbol);

                    this.ghostSymbol.getSpineTween(`animation`, true);
                },
                undefined,
                0
            );
        }

        return tl;
    }

    public override getHideNearWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(super.getHideNearWinAnimation());
        tl.call(
            () => {
                if (this.ghostSymbol) {
                    this.removeChild(this.ghostSymbol);
                }
            },
            undefined,
            0
        );

        return tl;
    }

    public clearCurrentSymbols(): void {
        this.currentSymbols.forEach((symbol) => {
            returnSymbolToPool(symbol);
        });

        super.clearCurrentSymbols();
    }

    public override handleSoundsOnLand() {
        super.handleSoundsOnLand();

        this.currentSymbols.forEach((symbol, symbolIndex) => {
            // if (symbol instanceof ApocalypseWildSymbol) {
            //     if (!symbol.isGold && symbolIndex > 1 && symbolIndex < 5) {
            //         playSoundOnce({
            //             name: ApocalypseSoundNames.SILVER_WILD_LAND,
            //             stereo: davidPanningReelStop[this.reelId],
            //             playOnceMinDuration: 2,
            //         });
            //     }
            // }
        });
    }

    public getXNudgeSymbol(): ApocalypseXNudgeSymbol {
        return this.currentSymbols.filter(
            (symbol, index) => symbol.name.includes(ApocalypseSymbolNames.XNUDGE) && index > 0
        )[0] as ApocalypseXNudgeSymbol;
    }

    public getXNudgeAnimation(shouldIdleAnimate: boolean = false): gsap.core.Timeline {
        const xNudgeSymbol: ApocalypseXNudgeSymbol = this.getXNudgeSymbol();

        const symbolsToNudgeDown: AbstractSymbol[] = [];

        this.getAllVisibleStopSymbols().forEach((symbol, index) => {
            if (
                symbol !== xNudgeSymbol &&
                xNudgeSymbol.positionInArray > 0 &&
                symbol.positionInArray > xNudgeSymbol.positionInArray
            ) {
                symbolsToNudgeDown.push(symbol);
            }
        });

        let playIdleAnimationFunction: Function | null = null;

        if (shouldIdleAnimate) {
            playIdleAnimationFunction = () => {
                xNudgeSymbol.getIdleAnimation();
            };
        }
        const timeline = gsap.timeline({
            smoothChildTiming: true,
            onStart: () => {
                this.symbolsContainer.addChild(xNudgeSymbol);
            },
            onComplete: () => {
                if (playIdleAnimationFunction !== null) {
                    playIdleAnimationFunction();
                }
            },
        });

        const extendNudgeAnimation = xNudgeSymbol.getXNudgeAnimation(
            ``,
            symbolsToNudgeDown.length + 1
        );
        xNudgeSymbol.size = symbolsToNudgeDown.length + 1;

        timeline.add(extendNudgeAnimation, 0);

        if (symbolsToNudgeDown.length > 0) {
            const moveSpineToNewPosition = gsap.to(xNudgeSymbol, {
                duration: extendNudgeAnimation.totalDuration(),
                y: xNudgeSymbol.y - 2,
            });
            timeline.add(moveSpineToNewPosition, 0);

            symbolsToNudgeDown.forEach((symbol, index) => {
                if (index < symbolsToNudgeDown.length) {
                    const minDelay = 0.35;
                    const delay = 0.3;

                    const nudgeDownTimeline = gsap.timeline({
                        smoothChildTiming: true,
                        autoRemoveChildren: true,
                        onStart: () => {
                            // playSound(WhackedSoundNames.FS_XNUDGE_DOWN);
                        },
                        onComplete: () => {
                            this.currentSymbols = this.currentSymbols.filter(
                                (currentSymbol) => currentSymbol !== symbol
                            );
                            returnSymbolToPool(symbol);
                        },
                    });

                    timeline.add(nudgeDownTimeline, minDelay + delay * index);
                }
            });
        }

        return timeline;
    }

    public switchHighSymbolToBlank(
        symbol: ApocalypseSymbol,
        duration: number = 1
    ): gsap.core.Tween {
        const newBlankSymbol = getSymbolFromPool(
            ApocalypseSymbolNames.BLANK
        ) as ApocalypseBlankSymbol;
        newBlankSymbol.reelPosition = symbol.reelPosition;
        newBlankSymbol.positionInArray = symbol.positionInArray;
        newBlankSymbol.position = symbol.position;
        newBlankSymbol.hasSpecialTexture = true;

        const symbolIndex = this.currentSymbols.indexOf(symbol);
        this.currentSymbols[symbolIndex] = newBlankSymbol;

        this.symbolsContainer.addChild(newBlankSymbol);
        this.symbolsContainer.addChild(symbol);

        return gsap.to(symbol, {
            duration: duration,
            alpha: 0,
            onComplete: () => {
                returnSymbolToPool(symbol);
            },
        });
    }

    public override resetOnStartSpin() {
        super.resetOnStartSpin();

        this.currentSymbols.forEach((symbol) => {
            symbol.resetOnStartSpin();
        });

        this.scatterSymbol = undefined;
        this.xNudgeSymbol = undefined;
    }
}

export default ApocalypseReel;
